import * as yup from 'yup'

import { i18WithDefault as t } from '@shared/locale'
import { Types } from 'service-api'
import { getTzDateInUtc } from 'components-ui/src/shared/dates'
import { MIN_DATE } from 'components-ui'

export const petSexOptions = [
  { id: 'MALE', label: 'Male' },
  { id: 'FEMALE', label: 'Female' },
  { id: 'null', label: 'Not specified' },
]

export type TPetSexOption = (typeof petSexOptions)[1]

export const getPetSexFromValue = (value?: string | null) => {
  const defaultValue = petSexOptions[2]
  if (!value) {
    return defaultValue
  }
  return petSexOptions.find((pt) => pt.id === value) || defaultValue
}

export const getSexFromOption = (value: TPetSexOption) => {
  if (value.id === 'null') {
    return null
  }
  return value.id
}

export const alteredOptions = [
  { id: 'true', label: 'Altered' },
  { id: 'false', label: 'Not Altered' },
  { id: 'null', label: 'Not Specified' },
]
export type TAlteredOption = (typeof alteredOptions)[1]

export const getAlteredFromValue = (value?: string | null) => {
  const defaultValue = alteredOptions[2]
  if (!value) {
    return defaultValue
  }
  return alteredOptions.find((ao) => ao.id === value) || defaultValue
}

export const getAlteredFromOption = (value: TAlteredOption) => {
  switch (value.id) {
    case 'true':
      return true
    case 'false':
      return false
    case 'null':
      return null
    default:
      return null
  }
}

export const petProfileSchema = yup.object({
  displayName: yup.string().max(140).required().label(t('profile.pet.form.displayName')),
  specie: yup
    .object({
      id: yup.string(),
      displayName: yup.string(),
    })
    .required()
    .label(t('profile.pet.form.specie')),
  breed: yup
    .object({
      id: yup.string(),
      displayName: yup.string(),
    })
    .required()
    .typeError(t('global.error.invalidValue'))
    .label(t('profile.pet.form.breed')),
  weight: yup.string().min(1).max(5).required().label(t('profile.pet.form.weight')),
  color: yup.string().max(255).label(t('profile.pet.form.color')),
  dob: yup
    .date()
    .nullable()
    .min(MIN_DATE, t('global.errors.invalidDate'))
    .typeError(t('global.error.invalidValue'))
    .label(t('profile.pet.form.dob')),
  sex: yup
    .object({
      id: yup.string(),
      label: yup.string(),
    })
    .required()
    .label(t('profile.pet.form.sex')),
  altered: yup
    .object({
      id: yup.string(),
      label: yup.string(),
    })
    .required()
    .label(t('profile.pet.form.altered')),
  vetName: yup.string().label(t('profile.pet.form.vetName')),
  vetPhone: yup.string().max(16).label(t('profile.pet.form.vetPhone')),
  clientCareInstruction: yup.string().max(1000).label(t('profile.pet.form.careInstruction')),
  clientFeedingInstruction: yup.string().max(1000).label(t('profile.pet.form.feedingInstruction')),
  clientGroomingInstruction: yup.string().max(1000).label(t('profile.pet.form.groomingInstruction')),
  clientMedicationInstruction: yup.string().max(1000).label(t('profile.pet.form.medicationInstruction')),
})

export type TPetProfileSchema = yup.InferType<typeof petProfileSchema>

export const setupInitialValues = (
  location: Types.LocationClientDto,
  defaultSpecie: Types.LocationSpeciesClientDto,
  lpp?: Types.LocationPetProfileClientDto
) => {
  const tmpWeight = parseInt(String(lpp?.weight ?? 0))
  const weight = isNaN(tmpWeight) || tmpWeight === 0 ? '' : String(tmpWeight)

  return {
    displayName: lpp?.displayName || '',
    specie: lpp?.locationSpecies || defaultSpecie,
    breed: lpp?.breed || null, // defaultBreed,
    weight: weight,
    color: lpp?.color || '',
    dob: !lpp ? null : !lpp.dateOfBirth ? null : getTzDateInUtc(lpp.dateOfBirth, null, location.timezone) || '',
    sex: getPetSexFromValue(lpp?.sex),
    altered: getAlteredFromValue(String(lpp?.altered)),
    vetName: lpp?.vetName || '',
    vetPhone: lpp?.vetPhone || '',
    clientCareInstruction: lpp?.clientCareInstruction || '',
    clientFeedingInstruction: lpp?.clientFeedingInstruction || '',
    clientGroomingInstruction: lpp?.clientGroomingInstruction || '',
    clientMedicationInstruction: lpp?.clientMedicationInstruction || '',
  } as unknown as TPetProfileSchema
}
